import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Text } from 'react-konva';
import { Html } from 'react-konva-utils';
import Konva from 'konva';
import { useTransformer } from './TransformerContext';

const TextBox = ({
  x = 100,
  y = 30,
  id,
  isSelected,
  onSelect,
  draggable,
  setTextProps,
  textColor,
  stroke,
  width: shapeWidth = 150,      // <-- comes from SHAPE
  height: shapeHeight = 80,     // <-- comes from SHAPE
  fontSize: fontSize = 18,
  onStartEdit,
  onMouseDownOnText,          // <-- will be used, but smarter,
  ...props
}) => {
  const [text, setText] = useState('Double\nclick to edit');
  const [editValue, setEditValue] = useState('Double\nclick to edit');
  const [isEditing, setIsEditing] = useState(false);

  const [fitFontSize, setFitFontSize] = useState(fontSize);

  const textRef = useRef(null);
  const textareaRef = useRef(null);
  const transformerRef = useTransformer();

  // track potential drag gesture
  const dragMetaRef = useRef({
    downPos: null,
    dragging: false,
  });

  // (x, y) is CENTER of the shape/box
  const shapeLeft = x - shapeWidth / 2;
  const shapeTop  = y - shapeHeight / 2;

  useEffect(() => {
    Konva._fixTextRendering = true;
  }, []);

  // Position Text and apply current width/height from shape
  useEffect(() => {
    if (!textRef.current) return;
    const node = textRef.current;
    node.x(shapeLeft);
    node.y(shapeTop);
    node.width(shapeWidth);
    node.height(shapeHeight);
    node.getLayer()?.batchDraw();
  }, [shapeLeft, shapeTop, shapeWidth, shapeHeight, text, fitFontSize, isEditing]);

  // Report size to parent if needed (optional)
  useEffect(() => {
    if (!isEditing && setTextProps && textRef.current) {
      setTextProps({
        width: textRef.current.width(),
        height: textRef.current.height(),
      });
    }
  }, [isEditing, setTextProps]);

  // Attach transformer for move/rotate
  useEffect(() => {
    const node = textRef.current;
    const tr = transformerRef.current;
    if (!node || !tr) return;

    if (isSelected) {
      tr.nodes([node]);
      tr.getLayer()?.batchDraw();
    }
  }, [isSelected, transformerRef]);

  // DOUBLE-CLICK: open textarea
  const handleDblClick = (e) => {
    if (e.target !== textRef.current) return;

    const node = textRef.current;
    const rect = node.getClientRect();

    if (typeof onStartEdit === 'function') {
      onStartEdit({
        id,
        width: shapeWidth,
        height: shapeHeight,
        nodeWidth: rect.width,
        nodeHeight: rect.height,
      });
    }

    setEditValue(text);
    setIsEditing(true);
  };

  // --- Auto-fit font size so all text stays inside shape box ---
  const recomputeFontSize = useCallback(() => {
    if (!textRef.current) return;

    const maxWidth = shapeWidth;
    const maxHeight = shapeHeight;

    const fontFamily = textRef.current.fontFamily() || 'Arial';
    const lineHeight = textRef.current.lineHeight() || 1.2;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const raw = text || '';
    const maxFont = fontSize;
    const minFont = 8;

    // simple word-wrap simulation to estimate total height
    const wrapText = (txt, size) => {
      ctx.font = `${size}px ${fontFamily}`;
      const paragraphs = txt.split('\n');
      const lines = [];

      paragraphs.forEach((para) => {
        const words = para.split(' ');
        let line = '';

        words.forEach((word) => {
          const testLine = line ? line + ' ' + word : word;
          const width = ctx.measureText(testLine).width;

          if (width > maxWidth && line) {
            lines.push(line);
            line = word;
          } else {
            line = testLine;
          }
        });

        lines.push(line);
      });

      return lines;
    };

    let size = maxFont;
    while (size >= minFont) {
      const lines = wrapText(raw, size);
      const linePixelHeight = size * lineHeight;
      const totalHeight = lines.length * linePixelHeight;

      if (totalHeight <= maxHeight) break;
      size -= 1;
    }

    if (size < minFont) size = minFont;
    setFitFontSize(size);
  }, [text, shapeWidth, shapeHeight, fontSize]);

  // Recompute whenever text or shape size changes
  useEffect(() => {
    recomputeFontSize();
  }, [recomputeFontSize]);

  // Textarea CSS: same box as shape, using fitFontSize
  const editorCss = useCallback(() => {
    return {
      position: 'absolute',
      top: shapeTop + 'px',
      left: shapeLeft + 'px',
      width: shapeWidth + 'px',
      height: shapeHeight + 'px',
      fontSize: fitFontSize + 'px',
      fontFamily: textRef.current?.fontFamily() || 'Arial',
      lineHeight: textRef.current?.lineHeight() || 1.2,
      padding: '4px',
      border: '2px solid #65b3ff',
      outline: 'none',
      background: 'transparent',
      resize: 'none',
      overflow: 'auto',      // can still scroll while typing
      whiteSpace: 'pre-wrap',
      textAlign: 'center',
    };
  }, [shapeLeft, shapeTop, shapeWidth, shapeHeight, fitFontSize]);

  // Cursor to end when editing starts
  useEffect(() => {
    if (isEditing && textareaRef.current) {
      const el = textareaRef.current;
      el.focus();
      const len = el.value.length;
      el.setSelectionRange(len, len);
    }
  }, [isEditing]);

  const commitEdit = () => {
    setText(editValue);
    setIsEditing(false);
  };

  const cancelEdit = () => {
    setEditValue(text);
    setIsEditing(false);
  };

  // ---- drag detection on text: only start drag when moving enough ----
  const handleMouseDown = (e) => {
    if (isEditing) return; // when editing, ignore drag
    const evt = e.evt;
    dragMetaRef.current.downPos = { x: evt.clientX, y: evt.clientY };
    dragMetaRef.current.dragging = false;
  };

  const handleMouseMove = (e) => {
    if (isEditing) return;
    const meta = dragMetaRef.current;
    if (!meta.downPos || meta.dragging) return;

    const evt = e.evt;
    const dx = evt.clientX - meta.downPos.x;
    const dy = evt.clientY - meta.downPos.y;
    const distSq = dx * dx + dy * dy;

    const DRAG_THRESHOLD_SQ = 4 * 4; // 4px movement

    if (distSq > DRAG_THRESHOLD_SQ) {
      // user is actually dragging → start dragging the shape
      meta.dragging = true;
      if (onMouseDownOnText) {
        onMouseDownOnText(e);
      }
    }
  };

  const handleMouseUp = () => {
    dragMetaRef.current.downPos = null;
    dragMetaRef.current.dragging = false;
  };

  return (
    <>
      <Text
        id={id}
        ref={textRef}
        x={shapeLeft}
        y={shapeTop}
        draggable={false} // text itself not draggable
        onClick={onSelect}
        onDblClick={handleDblClick}
        onDblTap={handleDblClick}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        text={text}
        width={shapeWidth}
        height={shapeHeight}
        fill={textColor}
        fontSize={fitFontSize}   // auto-fit
        wrap="word"
        ellipsis={false}         // show ALL text
        listening={!isEditing}
        visible={!isEditing}
        align="center"
        {...props}
        onDragMove={()=>console.log("Texting...")}
      />

      {isEditing && (
        <Html>
          <textarea
            ref={textareaRef}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={commitEdit}
            onKeyDown={(e) => {
              if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
                e.preventDefault();
                commitEdit();
              }
              if (e.key === 'Escape') {
                e.preventDefault();
                cancelEdit();
              }
            }}
            style={editorCss()}
          />
        </Html>
      )}
    </>
  );
};

export default TextBox;
